import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useSidebar } from '../context/SidebarContext';
import { useIsNarrow } from '../hooks/useIsNarrow';
import { useFoldSlide } from '../hooks/useFoldSlide';
import { Spinner } from '../components/Spinner';
import { useCrmHost } from './CrmHostContext';
import './CrmFrameHost.css';

/**
 * The one persistent CRM frame (ADR 0020). It lives at the app root, pinned
 * over the main area next to the sidebar, so switching routes never reloads
 * it: CRM routes fade it in, every other route fades it out and makes it
 * inert. A skeleton covers it until the CRM reports the requested page.
 */
export function CrmFrameHost() {
  const { t } = useTranslation('common');
  const { isAuthenticated } = useAuth();
  const { sidebarWidth } = useSidebar();
  const isNarrow = useIsNarrow();
  const crm = useCrmHost();
  const hostRef = useRef<HTMLDivElement>(null);
  useFoldSlide(hostRef, sidebarWidth, !isNarrow);

  if (!isAuthenticated || !crm.frameMounted) return null;

  const showSkeleton = crm.frameVisible && !crm.frameSettled && !crm.frameDenied;

  return (
    <div
      ref={hostRef}
      className={`crm-host ${crm.frameVisible ? 'is-visible' : ''} ${crm.target?.accountId ? 'has-rail' : ''}`}
      // After the sidebar, and the CRM page rail (60px) on account pages; phones: CSS.
      style={isNarrow ? undefined : { left: sidebarWidth + (crm.target?.accountId ? 60 : 0) }}
      aria-hidden={!crm.frameVisible}
      inert={!crm.frameVisible}
    >
      <iframe
        ref={crm.frameRef}
        className="crm-host-frame"
        src={crm.frameSrc}
        title={t('layout.crmMode')}
        sandbox="allow-forms allow-scripts allow-same-origin allow-popups allow-downloads allow-modals"
        allow="clipboard-read; clipboard-write; microphone"
      />
      {/* Kept mounted and faded by CSS: an unmount-on-exit would race the next
          target change; a class flip cannot. */}
      <div
        className={`crm-host-skeleton ${showSkeleton ? '' : 'is-hidden'}`}
        aria-label={t('crmFrame.loading')}
        role="status"
        aria-hidden={!showSkeleton}
      >
        <div className="crm-host-skel-nav">
          {Array.from({ length: 7 }).map((_, i) => (
            <span key={i} className="crm-host-skel-navitem" style={{ width: `${56 + ((i * 17) % 40)}%` }} />
          ))}
        </div>
        <div className="crm-host-skel-list">
          <span className="crm-host-skel-bar" style={{ width: '60%', height: 14, marginBottom: 16 }} />
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="crm-host-skel-chat">
              <span className="crm-host-skel-avatar" />
              <span className="crm-host-skel-lines">
                <span className="crm-host-skel-bar" style={{ width: `${45 + ((i * 23) % 40)}%` }} />
                <span className="crm-host-skel-bar crm-host-skel-bar--dim" style={{ width: `${60 + ((i * 31) % 35)}%` }} />
              </span>
            </div>
          ))}
        </div>
        <div className="crm-host-skel-thread">
          <div className="crm-host-skel-thread-body">
            {Array.from({ length: 6 }).map((_, i) => (
              <span
                key={i}
                className={`crm-host-skel-bubble ${i % 3 === 2 ? 'is-mine' : ''}`}
                style={{ width: `${30 + ((i * 29) % 35)}%` }}
              />
            ))}
          </div>
          <div className="crm-host-skel-loading">
            <Spinner size={18} label={t('crmFrame.loading')} />
          </div>
        </div>
      </div>
    </div>
  );
}
